import React, { useState, useEffect } from 'react';
import { padreService } from 'services/padreService';
import { estadoCuentaService } from 'services/pagoService';
import { reporteService } from 'services/reporteService';
import { periodoService } from 'services/periodoService';
import { documentoService } from 'services/documentoService';
import { htmlBoleta } from 'utils/documentoPdf';
import { exportarPDF } from 'utils/reporteExport';
import { HijoVinculado } from 'types/padre.types';
import { EstadoCuenta, PensionItem } from 'types/pago.types';
import { ReporteAsistenciaEstudiante } from 'types/reporte.types';
import { PeriodoResponse } from 'types/periodo.types';
import CalendarioAsistenciaDiaria from 'components/asistencia/CalendarioAsistenciaDiaria';
import { atencionPsicologicaService } from 'services/atencionPsicologicaService';
import {
  AtencionPsicologicaResponse,
  ESTADO_ATENCION_LABEL,
  ESTADO_ATENCION_COLOR,
  TIPO_ATENCION_LABEL,
} from 'types/psicologia.types';

// ============================================================
// MisHijosPage — Dashboard del apoderado
// ============================================================

type Tab = 'pagos' | 'asistencia-diaria' | 'asistencia' | 'boletas' | 'psicologia';

const SpinnerIcon = ({ size = 6 }: { size?: number }) => (
  <svg className={`animate-spin w-${size} h-${size} text-blue-600`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const sol = (n: number | null) =>
  n == null ? '—' :
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const PAGO_STYLE: Record<string, string> = {
  PAGADO:    'bg-green-100 text-green-700',
  PENDIENTE: 'bg-amber-100 text-amber-700',
  ANULADO:   'bg-slate-100 text-slate-500',
};

// ── Tab Pagos ─────────────────────────────────────────────────
const TabPagos: React.FC<{ hijo: HijoVinculado }> = ({ hijo }) => {
  const [cuenta, setCuenta] = useState<EstadoCuenta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    setLoading(true); setError(''); setCuenta(null);
    estadoCuentaService.getByEstudiante(hijo.estudianteId, hijo.anioEscolarId)
      .then(setCuenta)
      .catch(e => setError(e instanceof Error ? e.message : 'Error al cargar pagos'))
      .finally(() => setLoading(false));
  }, [hijo.estudianteId, hijo.anioEscolarId]);

  if (loading) return <div className="flex justify-center py-12"><SpinnerIcon /></div>;
  if (error)   return <p className="text-red-500 text-sm py-8 text-center">{error}</p>;
  if (!cuenta) return null;

  const pagadas   = cuenta.pensiones?.filter((p: PensionItem) => p.estado === 'PAGADO').length ?? 0;
  const pendientes = cuenta.pensiones?.filter((p: PensionItem) => p.estado === 'PENDIENTE').length ?? 0;

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-green-50 rounded-2xl px-4 py-3">
          <p className="text-xs font-semibold text-green-700 opacity-70">Pensiones pagadas</p>
          <p className="text-2xl font-bold text-green-700 mt-0.5">{pagadas}</p>
        </div>
        <div className="bg-amber-50 rounded-2xl px-4 py-3">
          <p className="text-xs font-semibold text-amber-700 opacity-70">Pendientes</p>
          <p className="text-2xl font-bold text-amber-700 mt-0.5">{pendientes}</p>
        </div>
        <div className="bg-slate-50 rounded-2xl px-4 py-3">
          <p className="text-xs font-semibold text-slate-500 opacity-70">Total pendiente</p>
          <p className="text-lg font-bold text-slate-700 mt-0.5 tabular-nums">
            {sol(cuenta.resumen?.totalPendiente ?? null)}
          </p>
        </div>
      </div>

      {/* Matrícula */}
      {cuenta.pagoMatricula && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Matrícula</span>
          </div>
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800">Pago de matrícula</p>
              <p className="text-xs text-slate-400 mt-0.5">
                {cuenta.pagoMatricula.fechaPago
                  ? `Pagado el ${cuenta.pagoMatricula.fechaPago}`
                  : 'Sin fecha de pago'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-slate-800 tabular-nums">
                {sol(cuenta.pagoMatricula.montoFinal)}
              </p>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PAGO_STYLE[cuenta.pagoMatricula.estado] ?? PAGO_STYLE.PENDIENTE}`}>
                {cuenta.pagoMatricula.estado}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Pensiones */}
      {cuenta.pensiones && cuenta.pensiones.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Pensiones mensuales</span>
            <span className="text-xs text-slate-400">{cuenta.pensiones.length} cuotas</span>
          </div>
          <ul className="divide-y divide-slate-50">
            {cuenta.pensiones.map((p: PensionItem) => (
              <li key={p.numeroPension} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Pensión {p.numeroPension}</p>
                  {p.fechaPago && (
                    <p className="text-xs text-slate-400">{p.fechaPago}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-800 tabular-nums">{sol(p.montoFinal)}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PAGO_STYLE[p.estado] ?? PAGO_STYLE.PENDIENTE}`}>
                    {p.estado}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Tab Asistencia ────────────────────────────────────────────
const TabAsistencia: React.FC<{ hijo: HijoVinculado }> = ({ hijo }) => {
  const [data, setData]     = useState<ReporteAsistenciaEstudiante | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    setLoading(true); setError(''); setData(null);
    reporteService.asistenciaEstudiante(hijo.estudianteId, hijo.anioEscolarId)
      .then(setData)
      .catch(e => setError(e instanceof Error ? e.message : 'Error al cargar asistencia'))
      .finally(() => setLoading(false));
  }, [hijo.estudianteId, hijo.anioEscolarId]);

  if (loading) return <div className="flex justify-center py-12"><SpinnerIcon /></div>;
  if (error)   return <p className="text-red-500 text-sm py-8 text-center">{error}</p>;
  if (!data || data.cursos.length === 0) {
    return <p className="text-slate-400 text-sm py-8 text-center">Sin registros de asistencia</p>;
  }

  const totalPresentes = data.cursos.reduce((s, c) => s + c.presentes, 0);
  const totalSesiones  = data.cursos.reduce((s, c) => s + c.totalSesiones, 0);
  const pctGeneral     = totalSesiones > 0 ? Math.round(totalPresentes / totalSesiones * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Resumen global */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center gap-5">
        <div
          className="w-16 h-16 rounded-full flex-shrink-0 flex items-center justify-center text-lg font-extrabold"
          style={{
            background: pctGeneral >= 85 ? '#dcfce7' : pctGeneral >= 70 ? '#fef9c3' : '#fee2e2',
            color:      pctGeneral >= 85 ? '#166534' : pctGeneral >= 70 ? '#854d0e' : '#991b1b',
          }}
        >
          {pctGeneral}%
        </div>
        <div>
          <p className="text-base font-bold text-slate-800">Asistencia general</p>
          <p className="text-xs text-slate-400">{totalPresentes} presencias de {totalSesiones} sesiones totales</p>
        </div>
      </div>

      {/* Por curso */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Por curso</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                {['Curso', 'Sesiones', 'Presente', 'Tardanza', 'Justificado', 'Falta', '%'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-bold text-slate-400 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.cursos.map(c => {
                const pct = c.totalSesiones > 0 ? Math.round(c.presentes / c.totalSesiones * 100) : 0;
                return (
                  <tr key={c.asignacionId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-800 whitespace-nowrap">{c.cursoNombre}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 text-center">{c.totalSesiones}</td>
                    <td className="px-4 py-3 text-sm text-green-600 font-semibold text-center">{c.presentes}</td>
                    <td className="px-4 py-3 text-sm text-amber-600 text-center">{c.tardanzas}</td>
                    <td className="px-4 py-3 text-sm text-blue-600 text-center">{c.justificados}</td>
                    <td className="px-4 py-3 text-sm text-red-500 text-center">{c.ausentes}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                        ${pct >= 85 ? 'bg-green-100 text-green-700' : pct >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {pct}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Tab Boletas ───────────────────────────────────────────────
const TabBoletas: React.FC<{ hijo: HijoVinculado }> = ({ hijo }) => {
  const [periodos, setPeriodos]       = useState<PeriodoResponse[]>([]);
  const [loading, setLoading]         = useState(true);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    setLoading(true);
    periodoService.listarPorAnioEscolar(hijo.anioEscolarId)
      .then(data => setPeriodos(data.filter(p => p.tipo === 'REGULAR')))
      .catch(() => setPeriodos([]))
      .finally(() => setLoading(false));
  }, [hijo.anioEscolarId]);

  const handleDescargar = async (p: PeriodoResponse) => {
    setDownloadingId(p.idPeriodo); setDownloadError('');
    try {
      const data = await documentoService.getBoleta(hijo.matriculaId, p.idPeriodo);
      exportarPDF(
        `Boleta — ${hijo.estudianteApellidos} ${hijo.estudianteNombres} — ${p.nombre}`,
        htmlBoleta(data),
      );
    } catch (e: unknown) {
      setDownloadError(e instanceof Error ? e.message : 'Error al generar la boleta');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><SpinnerIcon /></div>;

  return (
    <div className="space-y-4">
      {downloadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          {downloadError}
        </div>
      )}

      {periodos.length === 0 ? (
        <p className="text-slate-400 text-sm py-8 text-center">No hay períodos disponibles para este año</p>
      ) : (
        <div className="grid gap-3">
          {periodos.map(p => (
            <div key={p.idPeriodo}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-800">{p.nombre}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {p.fechaInicio && p.fechaFin
                    ? `${p.fechaInicio} — ${p.fechaFin}`
                    : `Año ${hijo.anio}`}
                </p>
              </div>
              <button
                onClick={() => handleDescargar(p)}
                disabled={downloadingId === p.idPeriodo}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                           text-white text-xs font-bold transition-colors disabled:opacity-60"
              >
                {downloadingId === p.idPeriodo
                  ? <SpinnerIcon size={4} />
                  : (
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z"/>
                    </svg>
                  )
                }
                Descargar
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-slate-400 text-center">
        Las boletas se generan en PDF con la información registrada en el sistema.
      </p>
    </div>
  );
};

// ── Tab Psicología (vista del padre) ──────────────────────────
const TabPsicologia: React.FC<{ hijo: HijoVinculado }> = ({ hijo }) => {
  const [items, setItems] = useState<AtencionPsicologicaResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError(''); setItems([]);
    atencionPsicologicaService.listarPorMatricula(hijo.matriculaId)
      .then(setItems)
      .catch(e => setError(e instanceof Error ? e.message : 'Error al cargar sesiones'))
      .finally(() => setLoading(false));
  }, [hijo.matriculaId]);

  if (loading) return <div className="flex justify-center py-12"><SpinnerIcon /></div>;
  if (error)   return <p className="text-red-500 text-sm py-8 text-center">{error}</p>;

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
        <p className="text-slate-400 text-sm">No hay sesiones psicológicas registradas</p>
      </div>
    );
  }

  // Ordenar por fecha desc
  const ordenados = [...items].sort((a, b) => b.fechaHora.localeCompare(a.fechaHora));
  const proximas = ordenados.filter(a => a.estado === 'PROGRAMADA');
  const pasadas  = ordenados.filter(a => a.estado !== 'PROGRAMADA');

  const Card = ({ a }: { a: AtencionPsicologicaResponse }) => (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm font-bold text-slate-800">
            {a.fechaHora.slice(0, 10)} · {a.fechaHora.slice(11, 16)}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {TIPO_ATENCION_LABEL[a.tipo]} · {a.duracionMin} min · con {a.psicologoApellidos}, {a.psicologoNombres}
          </p>
        </div>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${ESTADO_ATENCION_COLOR[a.estado]}`}>
          {ESTADO_ATENCION_LABEL[a.estado]}
        </span>
      </div>
      {a.resumenParaFamilia ? (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-1">Resumen para la familia</p>
          <p className="text-sm text-blue-900 whitespace-pre-line">{a.resumenParaFamilia}</p>
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic">
          Información confidencial — contactar al área de psicología.
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {proximas.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Próximas sesiones</p>
          {proximas.map(a => <Card key={a.idAtencionPsicologica} a={a} />)}
        </div>
      )}
      {pasadas.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Sesiones pasadas</p>
          {pasadas.map(a => <Card key={a.idAtencionPsicologica} a={a} />)}
        </div>
      )}
      <p className="text-xs text-slate-400 text-center">
        Las notas detalladas son confidenciales. Si necesita más información, contacte directamente al área de psicología.
      </p>
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────
const MisHijosPage: React.FC = () => {
  const [hijos, setHijos]   = useState<HijoVinculado[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<HijoVinculado | null>(null);
  const [tab, setTab]       = useState<Tab>('pagos');

  useEffect(() => {
    padreService.getMisHijos().then(data => {
      const activos = data.filter(h => h.activo).sort((a, b) => b.anio - a.anio);
      setHijos(activos);
      if (activos.length > 0) setSelected(activos[0]);
      setLoading(false);
    });
  }, []);

  const TABS: { key: Tab; label: string }[] = [
    { key: 'pagos',             label: 'Pagos' },
    { key: 'asistencia-diaria', label: 'Asistencia diaria' },
    { key: 'asistencia',        label: 'Por curso' },
    { key: 'boletas',           label: 'Boletas' },
    { key: 'psicologia',        label: 'Psicología' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <SpinnerIcon size={8} />
      </div>
    );
  }

  if (hijos.length === 0) {
    return (
      <div className="space-y-5">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
          <h2 className="text-2xl font-bold">Mis Hijos/as</h2>
          <p className="text-blue-200 mt-1">Seguimiento escolar de tus hijos</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
            className="w-14 h-14 text-slate-200 mx-auto mb-4">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"/>
          </svg>
          <p className="text-slate-500 font-semibold">No tienes hijos vinculados aún</p>
          <p className="text-slate-400 text-sm mt-1">
            Contacta a la secretaría para que vincule tu cuenta con la matrícula de tu hijo/a.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h2 className="text-2xl font-bold">Mis Hijos/as</h2>
        <p className="text-blue-200 mt-1">
          {hijos.length} hijo{hijos.length !== 1 ? 's' : ''} vinculado{hijos.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Selector de hijo (si hay más de uno) */}
      {hijos.length > 1 && (
        <div className="flex gap-3 flex-wrap">
          {hijos.map(h => (
            <button
              key={h.idPadreEstudiante}
              onClick={() => { setSelected(h); setTab('pagos'); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-all
                ${selected?.idPadreEstudiante === h.idPadreEstudiante
                  ? 'bg-blue-700 border-blue-700 text-white shadow-md'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50'}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0
                ${selected?.idPadreEstudiante === h.idPadreEstudiante ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'}`}>
                {h.estudianteNombres.charAt(0)}{h.estudianteApellidos.charAt(0)}
              </div>
              <div>
                <p className="font-bold text-sm leading-tight">
                  {h.estudianteApellidos}, {h.estudianteNombres}
                </p>
                <p className={`text-xs mt-0.5 ${selected?.idPadreEstudiante === h.idPadreEstudiante ? 'text-blue-100' : 'text-slate-400'}`}>
                  {h.gradoNombre} {h.seccionNombre} · {h.anio}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detalle del hijo seleccionado */}
      {selected && (
        <div className="space-y-4">
          {/* Info card del hijo */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-700 font-extrabold text-xl
                              flex items-center justify-center flex-shrink-0">
                {selected.estudianteNombres.charAt(0)}{selected.estudianteApellidos.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-extrabold text-slate-800 leading-tight">
                  {selected.estudianteApellidos}, {selected.estudianteNombres}
                </p>
                <p className="text-sm text-slate-500 mt-0.5">DNI {selected.estudianteDni}</p>
              </div>
              <div className="text-right flex-shrink-0 hidden sm:block">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${NIVEL_COLOR[selected.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                  {selected.gradoNivel}
                </span>
                <p className="text-sm font-semibold text-slate-700 mt-1">
                  {selected.gradoNombre} — Sección {selected.seccionNombre}
                </p>
                <p className="text-xs text-slate-400">Año escolar {selected.anio}</p>
              </div>
            </div>
            {/* Info grado/sección en móvil */}
            <div className="mt-3 flex items-center gap-2 sm:hidden">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${NIVEL_COLOR[selected.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                {selected.gradoNivel}
              </span>
              <span className="text-xs text-slate-600 font-semibold">
                {selected.gradoNombre} — Sec. {selected.seccionNombre} · {selected.anio}
              </span>
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all
                  ${tab === t.key
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Contenido del tab */}
          <div>
            {tab === 'pagos'             && <TabPagos      hijo={selected} />}
            {tab === 'asistencia-diaria' && <CalendarioAsistenciaDiaria matriculaId={selected.matriculaId} />}
            {tab === 'asistencia'        && <TabAsistencia hijo={selected} />}
            {tab === 'boletas'           && <TabBoletas    hijo={selected} />}
            {tab === 'psicologia'        && <TabPsicologia hijo={selected} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default MisHijosPage;
